/* eslint-disable max-lines */
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Select } from '@ngxs/store';
import { ToastService } from 'common/services/toast.service';
import { AccountStatusState } from 'common/store/account-status/account-status.state';
import { CreateUserDetails, UserAuthorizedFeatures, UserAuthorizedRoles, UserDetails, UserDetailsEntity, UserFeatures, UserSecurityRoles, UserCreateStrings } from 'private/app/models/user.model';
import { UserAdminCreateService } from 'private/app/services/account-admin/user/user-admin-create.service';
import { UserAdminService } from 'private/app/services/account-admin/user/user-admin.service';
import { UserApiService } from 'private/app/services/account-admin/user/user-api.service';
import { EditUserSecurityHelperService } from 'private/app/views/account-admin-results/user/edit-user-security/edit-user-security-helper.service';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Component({
    selector: 'hvac-user-roles-details',
    templateUrl: './hvac-user-roles-details.component.html',
    styleUrls: ['./hvac-user-roles-details.component.scss']
})
export class HvacUserRolesDetailsComponent {
    @Input() usersData?: UserDetailsEntity;
    @Input() userDetails: UserDetails;
    @Input() userRoles: UserAuthorizedRoles[];
    @Input() userFeatures: UserAuthorizedFeatures[];
    @Input() page: string;

    @Output() registrationUserChangePage = new EventEmitter<string>();
    @Output() activeScreenName = new EventEmitter<string>();
    @Output() userDetailsSubmit = new EventEmitter<CreateUserDetails>();
    @Select(AccountStatusState.getUserBrandFamily) userBrandFamily$: Observable<string | null>;

    public details!: UserDetailsEntity;

    public authorizedRoles: UserAuthorizedRoles[] = [];
    public authorizedFeatures: UserAuthorizedFeatures[] = [];
    public visibleAuthorizedRoles: UserAuthorizedRoles[] = [];

    public selectedRoleIds = new Set<number>();
    public selectedFeatureIds = new Set<number>();

    public selectedRoleTab: number = 0;
    public selectedFeatureTab: number = 0;

    public currentRoles: UserSecurityRoles[] = [];
    public currentFeatures: UserFeatures[] = [];

    public summary = {
        defaultRoles: [] as UserSecurityRoles[],
        selectedRoles: [] as UserSecurityRoles[],
        roleFeatures: [] as UserFeatures[],
        additionalFeatures: [] as UserFeatures[]
    };

    public isUserDetailsAvailable: boolean = true;
    public isuserEditLastPage: boolean = false;
    public userRegistrationPage2: boolean = true;
    public userRegistrationPage3: boolean = false;
    public userRegistrationPage4: boolean = false;

    public isUserDetailsSummeryMode: boolean = true;
    public hvacCompanyId?: string;
    public isContactOnlySelected: boolean;
    public brandFamily: string | null;
    public isLoading: boolean = false;
    public userRoleAndFeatureDetails: boolean = false;
    public selctedDisplayName: string = '';
    public selectedDesc: string = '';
    public noAuthorizedRoles: boolean;
    public noAuthorizedFeatures: boolean;

    constructor(
        public userAPIService: UserApiService,
        public userAdminService: UserAdminService,
        public userAdminCreateService: UserAdminCreateService,
        private toastService: ToastService,
        public translate: TranslateService,
        private securityHelper: EditUserSecurityHelperService
    ) { }

    ngOnInit(): void {
        queueMicrotask(() => this.getActiveScreenName());
        this.userAdminService.smoothScrollToTop();
        this.hvacCompanyId = this.userDetails?.companyId;
        this.isContactOnlySelected = Boolean(this.userAdminCreateService.userCreateForm.controls.contactOnly.value);

        if (this.userDetails) {
            this.userDetails.contactOnly = this.isContactOnlySelected;
        }

        this.userBrandFamily$.subscribe((brand) => this.brandFamily = brand);
        this.loadData();
    }

    loadData(): void {
        if (!this.hvacCompanyId) {
            return;
        }

        this.isLoading = true;
        this.isUserDetailsAvailable = false;
        this.noAuthorizedRoles = false;
        this.noAuthorizedFeatures = false;

        forkJoin([
            this.userAPIService.getUserAuthorizedRoles(this.hvacCompanyId, this.isContactOnlySelected).pipe(
                catchError((error: HttpErrorResponse) => {
                    this.noAuthorizedRoles = error?.status === 404;
                    this.toastService.raiseDefaultErrorToast('HvacUserRolesDetailsComponent-getUserAuthorizedRoles', error?.error?.message);

                    return of([] as UserAuthorizedRoles[]);
                })
            ),
            this.userAPIService.getUserAuthorizedFeatures(this.hvacCompanyId, this.isContactOnlySelected).pipe(
                catchError((error: HttpErrorResponse) => {
                    this.noAuthorizedFeatures = error?.status === 404;
                    this.toastService.raiseDefaultErrorToast('HvacUserRolesDetailsComponent-getUserAuthorizedFeatures', error?.error?.message);

                    return of([] as UserAuthorizedFeatures[]);
                })
            )
        ]).subscribe({
            next: ([roles, features]) => {
                this.authorizedRoles = this.normalizeAuthorizedList(roles);
                this.authorizedFeatures = this.normalizeAuthorizedList(features);
                this.visibleAuthorizedRoles = this.authorizedRoles.filter(
                    (category) => category.roleCategory !== UserCreateStrings.SystemDefaults
                );
                this.initializeComponent();
                this.isLoading = false;
                this.isUserDetailsAvailable = true;
            },
            error: () => {
                this.isLoading = false;
                this.isUserDetailsAvailable = true;
            }
        });
    }

    initializeComponent(): void {
        this.details = this.buildSeedDetails();

        const state = this.securityHelper.initializeSelections(this.details, this.authorizedRoles, this.authorizedFeatures);

        this.selectedRoleIds = state.selectedRoleIds;
        this.selectedFeatureIds = state.selectedFeatureIds;

        this.refreshView();
    }

    buildSeedDetails(): UserDetailsEntity {
        if (this.usersData) {
            return this.usersData;
        }

        const systemDefaultRoles = this.authorizedRoles.find((category) => category.roleCategory === UserCreateStrings.SystemDefaults)?.userRoles
            .map((role) => ({
                ...role,
                roleCategoryId: 7
            })) || [];

        return {
            id: '',
            userID: this.userDetails?.userID || '',
            companyId: this.userDetails?.companyId || '',
            employerName: this.userDetails?.employerName || '',
            hvacPin: '',
            firstName: this.userDetails?.firstName || '',
            lastName: this.userDetails?.lastName || '',
            email: this.userDetails?.email || '',
            userAddress1: this.userDetails?.userAddress1 || '',
            userAddress2: this.userDetails?.userAddress2 || '',
            userCity: this.userDetails?.userCity || '',
            userCountry: this.userDetails?.userCountry || '',
            userPhone: this.userDetails?.userPhone || '',
            userPostalCode: this.userDetails?.userPostalCode || '',
            userState: this.userDetails?.userState || '',
            userStatus: '',
            lastLoginDate: '',
            lastValidatedDate: '',
            isValidationRequired: false,
            resetPassword: false,
            brandFamily: this.brandFamily || '',
            contactOnly: this.isContactOnlySelected,
            userTitle: this.userDetails?.userTitle || '',
            companyName: '',
            userCellPhone: this.userDetails?.userCellPhone || '',
            userFax: this.userDetails?.userFax || '',
            userRolesDetails: systemDefaultRoles,
            additionalFeatures: []
        };
    }

    refreshView(): void {
        this.refreshCurrentRoles();
        this.refreshCurrentFeatures();
        this.rebuildSummary();
    }

    refreshCurrentRoles(): void {
        if (!this.visibleAuthorizedRoles.length) {
            this.currentRoles = [];

            return;
        }

        this.currentRoles = this.visibleAuthorizedRoles[this.selectedRoleTab]?.userRoles || [];
    }

    refreshCurrentFeatures(): void {
        if (!this.authorizedFeatures.length) {
            this.currentFeatures = [];

            return;
        }

        const roleFeatureIds = this.securityHelper.getRoleFeatureIds(
            this.details,
            this.authorizedRoles,
            this.selectedRoleIds
        );

        this.currentFeatures = this.authorizedFeatures[this.selectedFeatureTab]?.userFeatures
            .filter((feature) => !roleFeatureIds.has(feature.featureId)) || [];
    }

    rebuildSummary(): void {
        this.summary = this.securityHelper.buildSummary(
            this.details,
            this.authorizedRoles,
            this.authorizedFeatures,
            this.selectedRoleIds,
            this.selectedFeatureIds
        );
    }

    toggleRole(role: UserSecurityRoles): void {
        if (this.selectedRoleIds.has(role.roleId)) {
            this.selectedRoleIds.delete(role.roleId);
        }
        else {
            this.selectedRoleIds.add(role.roleId);
        }

        const roleFeatureIds = this.securityHelper.getRoleFeatureIds(
            this.details,
            this.authorizedRoles,
            this.selectedRoleIds
        );

        this.selectedFeatureIds = this.securityHelper.removeConflictingFeatures(this.selectedFeatureIds, roleFeatureIds);
        this.refreshView();
    }

    toggleFeature(feature: UserFeatures): void {
        if (this.selectedFeatureIds.has(feature.featureId)) {
            this.selectedFeatureIds.delete(feature.featureId);
        }
        else {
            this.selectedFeatureIds.add(feature.featureId);
        }

        this.rebuildSummary();
    }

    isRoleSelected(role: UserSecurityRoles): boolean {
        return this.selectedRoleIds.has(role.roleId);
    }

    isFeatureSelected(feature: UserFeatures): boolean {
        return this.selectedFeatureIds.has(feature.featureId);
    }

    onRoleTabChange(index: number): void {
        this.selectedRoleTab = index;
        this.refreshCurrentRoles();
    }

    onFeatureTabChange(index: number): void {
        this.selectedFeatureTab = index;
        this.refreshCurrentFeatures();
    }

    trackByRole(_index: number, role: UserSecurityRoles): number {
        return role.roleId;
    }

    trackByFeature(_index: number, feature: UserFeatures): number {
        return feature.featureId;
    }

    hasRolesInCurrentTab(): boolean {
        return this.currentRoles.length > 0;
    }

    hasFeaturesInCurrentTab(): boolean {
        return this.currentFeatures.length > 0;
    }

    public getActiveScreenName() {
        let activeScreenName = '';

        if (this.page === UserCreateStrings.CreateUser) {
            if (this.userRegistrationPage2) {
                activeScreenName = this.translate.instant('ACCOUNT_ADMIN.USER_CREATE_PAGE.STEP_4');
            }
            else if (this.userRegistrationPage3) {
                activeScreenName = this.translate.instant('ACCOUNT_ADMIN.USER_CREATE_PAGE.STEP_5');
            }
            else if (this.userRegistrationPage4) {
                activeScreenName = this.translate.instant('ACCOUNT_ADMIN.USER_CREATE_PAGE.STEP_6');
            }
        }
        else if (this.page === UserCreateStrings.RegisterUser) {
            if (this.userRegistrationPage2) {
                activeScreenName = this.translate.instant('ACCOUNT_ADMIN.USER_REGISTRATIONS_PAGE.STEP_2');
            }
            else if (this.userRegistrationPage3) {
                activeScreenName = this.translate.instant('ACCOUNT_ADMIN.USER_REGISTRATIONS_PAGE.STEP_3');
            }
            else if (this.userRegistrationPage4) {
                activeScreenName = this.translate.instant('ACCOUNT_ADMIN.USER_REGISTRATIONS_PAGE.STEP_4');
            }
        }

        this.activeScreenName.emit(activeScreenName);
    }

    public onChangePage() {
        this.isuserEditLastPage = !this.isuserEditLastPage;
        this.userAdminService.smoothScrollToTop();
    }

    public getUserDetailsSummery() {
        this.isUserDetailsSummeryMode = !this.isUserDetailsSummeryMode;
    }

    public goToUserDetails() {
        this.userAdminCreateService.backToPreviousPageStep3 = true;
        (this.page === UserCreateStrings.CreateUser)
            ? this.registrationUserChangePage.emit('step3_userDetails')
            : this.registrationUserChangePage.emit('step1_userRegistrationForm');
    }

    public goToUserRoles() {
        this.userRegistrationPage2 = true;
        this.userRegistrationPage3 = false;
        this.userRegistrationPage4 = false;
        this.userAdminService.smoothScrollToTop();
        this.getActiveScreenName();
    }

    public goToUserFeatures() {
        this.userRegistrationPage2 = false;
        this.userRegistrationPage3 = true;
        this.userRegistrationPage4 = false;
        this.refreshCurrentFeatures();
        this.userAdminService.smoothScrollToTop();
        this.getActiveScreenName();
    }

    public goToUserSummary() {
        this.userRegistrationPage2 = false;
        this.userRegistrationPage3 = false;
        this.userRegistrationPage4 = true;
        this.userAdminService.smoothScrollToTop();
        this.getActiveScreenName();
    }

    public mapCreateUserDetails(userDetails: UserDetails): CreateUserDetails {
        return this.userAdminService.createUserPayloadDetails(
            userDetails,
            this.page,
            this.brandFamily,
            [...this.summary.defaultRoles, ...this.summary.selectedRoles],
            this.summary.additionalFeatures
        );
    }

    public onSubmit() {
        this.userAdminService.smoothScrollToTop();
        this.userDetailsSubmit.emit(this.mapCreateUserDetails(this.userDetails));
    }

    public onSelectRoleAndFeatureDetails(role: UserSecurityRoles | null, feature: UserFeatures | null) {
        if (role) {
            this.selctedDisplayName = role.roleDisplayName;
            this.selectedDesc = role.roleDesc || '';
        }

        if (feature) {
            this.selctedDisplayName = feature.featureDisplayName;
            this.selectedDesc = feature.featureDesc || '';
        }

        this.userRoleAndFeatureDetails = true;
    }

    public userRoleAndFeatureModalClose() {
        this.userRoleAndFeatureDetails = false;
    }

    private normalizeAuthorizedList<T>(items: T[] | T | null | undefined): T[] {
        if (Array.isArray(items)) {
            return items;
        }

        if (items) {
            return [items];
        }

        return [];
    }
}
